import { useState, useCallback } from 'react';
import { chatbotService } from '../services/chatbot';
import { ChatMessage } from '../types';

export const useChatbot = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);

  const sendMessage = useCallback(async (message: string, conversationId?: string) => {
    try {
      setIsLoading(true);
      setError(null);

      // Add user message to the conversation
      const userMessage: ChatMessage = {
        id: Date.now().toString(),
        message,
        timestamp: new Date().toISOString(),
        type: 'user',
        conversationId,
      };

      setMessages((prev) => [...prev, userMessage]);

      const response = await chatbotService.sendMessage(message, conversationId);

      if (response.success && response.data) {
        setMessages((prev) => [...prev, response.data!.response]);
        setSuggestions(response.data!.suggestions || []);
      } else {
        setError(response.error || 'Failed to send message');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send message');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const quickAction = useCallback(async (action: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await chatbotService.quickAction(action);

      if (response.success && response.data) {
        setMessages((prev) => [...prev, response.data!.response]);
      } else {
        setError(response.error || 'Failed to perform quick action');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to perform quick action');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setSuggestions([]);
    setError(null);
  }, []);

  return {
    messages,
    suggestions,
    isLoading,
    error,
    sendMessage,
    quickAction,
    clearMessages,
  };
};
